import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Trash2, Pencil, Phone } from "lucide-react";

export default function SuppliersPage() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", phone: "", email: "", address: "" });

  const load = async () => {
    const { data } = await api.get("/suppliers");
    setItems(data);
  };
  useEffect(() => { load(); }, []);

  const startCreate = () => { setEditing(null); setForm({ name: "", phone: "", email: "", address: "" }); setOpen(true); };
  const startEdit = (s) => { setEditing(s); setForm({ name: s.name, phone: s.phone, email: s.email || "", address: s.address || "" }); setOpen(true); };

  const save = async () => {
    try {
      if (editing) await api.patch(`/suppliers/${editing.id}`, form);
      else await api.post("/suppliers", form);
      toast.success("Kaydedildi");
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const del = async (id) => {
    if (!window.confirm("Silmek istediğinize emin misiniz?")) return;
    await api.delete(`/suppliers/${id}`);
    load();
  };

  return (
    <div className="fade-up">
      <div className="flex items-end justify-between mb-5">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight">Tedarikçiler</h1>
          <p className="text-sm text-slate-500 mt-1">{items.length} tedarikçi kayıtlı</p>
        </div>
        <Button data-testid="add-supplier-btn" className="h-10 gap-2 bg-slate-900 hover:bg-slate-800" onClick={startCreate}>
          <Plus className="h-4 w-4" /> Ekle
        </Button>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg divide-y divide-slate-100">
        {items.map((s) => (
          <div key={s.id} data-testid={`supplier-row-${s.id}`} className="p-4 flex items-center justify-between gap-3">
            <div className="min-w-0">
              <div className="font-medium text-slate-900">{s.name}</div>
              <div className="text-xs text-slate-500 flex items-center gap-3 mt-0.5 flex-wrap">
                <span className="inline-flex items-center gap-1"><Phone className="h-3 w-3" /> {s.phone}</span>
                {s.email && <span>{s.email}</span>}
              </div>
            </div>
            <div className="flex gap-1">
              <button data-testid={`edit-supplier-${s.id}`} className="p-2 rounded-md hover:bg-slate-100 text-slate-500" onClick={() => startEdit(s)}><Pencil className="h-4 w-4" /></button>
              <button data-testid={`delete-supplier-${s.id}`} className="p-2 rounded-md hover:bg-rose-50 text-rose-500" onClick={() => del(s.id)}><Trash2 className="h-4 w-4" /></button>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editing ? "Tedarikçiyi Düzenle" : "Yeni Tedarikçi"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider">Ad</Label>
              <Input data-testid="supplier-form-name" className="mt-1.5 h-11" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider">Telefon <span className="text-slate-400 normal-case font-normal">(ülke kodlu, örn: 905321112233)</span></Label>
              <Input data-testid="supplier-form-phone" className="mt-1.5 h-11" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider">E-posta</Label>
              <Input data-testid="supplier-form-email" type="email" className="mt-1.5 h-11" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider">Adres</Label>
              <Textarea data-testid="supplier-form-address" className="mt-1.5" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Vazgeç</Button>
            <Button data-testid="supplier-form-save" className="bg-slate-900 hover:bg-slate-800" onClick={save}>Kaydet</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
